import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { Img } from "components";
import Lottie from "lottie-react";
import Thinking from "assets/images/Thinking.json";
import ImageScan from "assets/images/ImageScan.json";
import SuccessModal from "../Communication/SuccessModal";

const FOTOR_ACCESS_KEY = "sk_wK3iRjOwNEosEZ2QcLgCQIpe";
const DEFAULT_BEFORE_IMAGE = "/images/soomuch-demo/BeforePreview.jpg";
const DEFAULT_AFTER_IMAGE = "/images/soomuch-demo/AfterPreview.jpg";
const DEFAULT_BEFORE_IMAGE2 = "/images/soomuch-demo/BeforePreview2.jpg";
const DEFAULT_AFTER_IMAGE2 = "/images/soomuch-demo/AfterPreview2.jpg";
const DEFAULT_BEFORE_IMAGE3 = "/images/soomuch-demo/BeforePreview3.jpg";
const DEFAULT_AFTER_IMAGE3 = "/images/soomuch-demo/AfterPreview3.jpg";
const DEFAULT_BEFORE_IMAGE4 = "/images/soomuch-demo/BeforePreview4.png";
const DEFAULT_AFTER_IMAGE4 = "/images/soomuch-demo/AfterPreview4.png";

const SAMPLES = [
  { before: DEFAULT_BEFORE_IMAGE, after: DEFAULT_AFTER_IMAGE },
  { before: DEFAULT_BEFORE_IMAGE2, after: DEFAULT_AFTER_IMAGE2 },
  { before: DEFAULT_BEFORE_IMAGE3, after: DEFAULT_AFTER_IMAGE3 },
  { before: DEFAULT_BEFORE_IMAGE4, after: DEFAULT_AFTER_IMAGE4 },
];

interface ImageValue {
  originalImage: string;
  enhancedImage: string;
  selectedImage: string;
}

interface FotorImageUploadProps {
  onChange?: (imageValue: ImageValue) => void;
  imageValue?: ImageValue;
  disable?: boolean;
  isError?: boolean;
}

const FotorImageUpload: React.FC<FotorImageUploadProps> = ({
  onChange,
  imageValue: externalImageValue,
  disable = false,
  isError = false
}) => {
  const [imageValue, setImageValue] = useState<ImageValue>({
    originalImage: "",
    enhancedImage: "",
    selectedImage: ""
  });
  const [enhancementStatus, setEnhancementStatus] = useState({
    processing: false,
    taskId: ""
  });
  const [enhancementError, setEnhancementError] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(1);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadController, setUploadController] = useState<AbortController | null>(null);
  const [selectedSample, setSelectedSample] = useState<number | null>(null);
  const [fileType, setFileType] = useState("");
  const [dummyLoader, setDummyLoader] = useState(false);
  const [successModal, setSuccessModal] = useState<any>(false);

  useEffect(() => {
    if (selectedSample) {
      // Set to true on mount
      setDummyLoader(true);

      // Then set to false after 3 seconds
      const timer = setTimeout(() => {
        setDummyLoader(false);
      }, 3000);

      // Optional cleanup in case the component unmounts before timeout
      return () => clearTimeout(timer);
    }
  }, [selectedSample]);

  useEffect(() => {
    if (externalImageValue) {
      setImageValue(externalImageValue);
      setSelectedSample(null);
    }
  }, [externalImageValue]);

  useEffect(() => {
    if (imageValue.originalImage && !selectedSample) {
      handleEnhanceImage();
    }
  }, [imageValue.originalImage, selectedSample]);

  useEffect(() => {
    if (enhancementStatus.processing) {
      setAnalysisStep(1);
      const timer1 = setTimeout(() => setAnalysisStep(2), 2000);
      return () => clearTimeout(timer1);
    }
  }, [enhancementStatus.processing]);

  const handleSampleClick = (sampleIndex: number) => {
    setSelectedSample(sampleIndex);
    setImageValue({
      originalImage: "",
      enhancedImage: SAMPLES[sampleIndex].after,
      selectedImage: ""
    });
  };

  const handleImageUpload = async (file: File) => {
    const controller = new AbortController();
    setUploadController(controller);
    setIsUploading(true);
    setEnhancementError(false);
    setFileType(file.name.split('.').pop()?.toLowerCase() || "");

    // Store current sample before resetting
    const currentSample = selectedSample;

    const progressInterval = setInterval(() => {
      setUploadProgress(prev => (prev >= 95 ? 95 : prev + 5));
    }, 100);

    const formData = new FormData();
    formData.append("files", file);

    try {
      const response = await axios.post(
        "https://stg.soomuch.ai/api/v1/master/upload/tempfiles",
        formData,
        { signal: controller.signal }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      const newImageValue = {
        originalImage: response.data?.data?.[0],
        selectedImage: response.data?.data?.[0],
        enhancedImage: ""
      };

      // Clear sample only after successful upload
      setSelectedSample(null);
      setImageValue(newImageValue);
      onChange?.(newImageValue);
    } catch (error) {
      // Restore sample selection if upload fails
      setSelectedSample(currentSample);
      setSuccessModal(successModal => ({ key: "error", message: (error?.response?.data?.message || "Failed to upload image") }));
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 500);
    }
  };

  const handleEnhanceImage = async () => {
    if (!imageValue?.originalImage) return;

    try {
      setEnhancementStatus({ processing: true, taskId: "" });
      setEnhancementError(false);

      const response = await axios.post(
        "https://stg.soomuch.ai/api/v1/master/fr/imageEnhance",
        {
          user_image_url: imageValue.originalImage,
          face_upsample: true,
          background_enhance: true,
          upscale: 2,
          fidelity_weight: 0.5,
        },
        {
          headers: {
            Authorization: `Bearer ${FOTOR_ACCESS_KEY}`,
            "Content-Type": "application/json",
          },
        }
      );

      const taskId = response.data?.data;
      if (!taskId) throw new Error("No task ID returned from enhancement API");

      console.log("[ENHANCE] Enhancement task submitted. Task ID:", taskId);
      setEnhancementStatus({ processing: true, taskId });

      startPollingEnhancementStatus(taskId);
    } catch (error) {
      setSuccessModal(successModal => ({ key: "error", message: (error?.response?.data?.message || "Unknown error occurred") }));
      setEnhancementStatus({ processing: false, taskId: "" });
      setEnhancementError(true);
    }
  };

  const startPollingEnhancementStatus = (taskId) => {
    let attempts = 0;
    const maxAttempts = 20;

    const intervalId = setInterval(async () => {
      try {
        console.log(`[POLLING] Attempt #${attempts + 1}`);
        const res = await axios.get(`https://api-b.fotor.com/v1/aiart/tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${FOTOR_ACCESS_KEY}` },
        });

        const data = res.data?.data;
        console.log("[POLLING] Response data:", data);

        const status = data?.status;
        const resultUrl = data?.resultUrl;

        if (status === 1 && resultUrl) {
          clearInterval(intervalId);
          console.log("[POLLING] Enhancement completed:", resultUrl);

          setImageValue({
            originalImage: imageValue.originalImage,
            selectedImage: imageValue.originalImage,
            enhancedImage: resultUrl
          });
          setEnhancementStatus({ processing: false, taskId: "" });
        } else if (status === 2) {
          clearInterval(intervalId);
          setEnhancementError(true);
          console.error("[POLLING] Enhancement failed.");
          setSuccessModal(successModal => ({ key: "error", message: "Fotor task could not complete successfully" }));
          setEnhancementStatus({ processing: false, taskId: "" });
        } else {
          console.log(`[POLLING] Status: ${status}. Still processing...`);
        }

        if (++attempts >= maxAttempts) {
          setEnhancementError(true);
          clearInterval(intervalId);
          console.warn("[POLLING] Max attempts reached without success.");
          setEnhancementStatus({ processing: false, taskId: "" });
          setSuccessModal(successModal => ({ key: "error", message: "It's taking longer than expected. Please try again later." }));
        }
      } catch (err) {
        clearInterval(intervalId);
        setEnhancementError(true);
        console.error("[POLLING ERROR]", err);
        setEnhancementStatus({ processing: false, taskId: "" });
        setSuccessModal(successModal => ({ key: "error", message: (err?.response?.data?.message || "Polling failed unexpectedly") }));
      }
    }, 3000);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: files => files[0] && handleImageUpload(files[0]),
    accept: {
      'image/jpeg': [],
      'image/png': []
    },
    multiple: false,
    disabled: disable || isUploading
  });

  // Modified progress bar JSX
  const ProgressBar = () => (
    <div className="flex items-center justify-between w-full gap-[1vh]">
      <div className="flex items-center gap-[1vh] min-w-0">
        <Img
          src={fileType === "png" ? "images/soomuch-demo/png.png" : "images/soomuch-demo/jpg.svg"}
          className="h-[4vh] w-[4vh]"
        />
        <div className="text-[1.8vh] font-medium truncate">
          {imageValue.originalImage.split('/').pop()}
        </div>
      </div>
      <div className="flex items-center gap-[1vh] w-full max-w-[20vh]">
        <div className="text-[1.8vh] font-medium">{Math.round(uploadProgress)}%</div>
        <div className="w-full bg-gray-200 rounded-full h-[0.5vh]">
          <div
            className="bg-blue-500 h-[0.5vh] rounded-full"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      </div>
    </div>
  );

  const ImageAnalysingScreen = () => (
    <div className="w-full min-h-[40vh] max-h-[40vh] flex flex-col items-center justify-center p-[1vh] bg-[url(/public/images/soomuch-demo/LoaderCardBackground.svg)] bg-no-repeat bg-cover bg-center rounded-[1vh]">
      <div className="mb-[1vh] w-[10vh] h-[10vh]">
        <Lottie animationData={ImageScan} className="w-full h-full" />
      </div>
      <h2 className="text-center text-[2.5vh] font-bold text-blue-900 mb-[0.5vh]">
        Analysing your <br /> image...
      </h2>
      <p className="text-blue-800 text-[2vh] text-center">
        Zooming in…🔍 Let’s see how <br /> sharp your image is!
      </p>
    </div>
  );

  const LowResolutionScreen = () => (
    <div className="w-full min-h-[40vh] max-h-[40vh] flex flex-col items-center justify-center p-4 text-center bg-[url(/public/images/soomuch-demo/LoaderCardBackground.svg)] bg-no-repeat bg-cover bg-center backdrop-blur-md rounded-xl">
      <div className="mb-4 w-[100px] h-[100px]">
        <Lottie
          animationData={Thinking}
          className="w-full h-full"
        />
      </div>
      <h2 className="text-center text-[2.5vh] font-bold text-[#103768] mb-2 px-4">
        We detected low resolution
      </h2>
      <p className="text-blue-800 text-[2.2vh] text-center">
        Sit back and relax, your image is being remastered!
      </p>
    </div>
  );

  const EnhancementErrorScreen = () => (
    <div className="w-full min-h-[40vh] max-h-[40vh] flex flex-col items-center justify-center gap-4 p-4 text-center bg-[url(/public/images/soomuch-demo/LoaderCardBackground.svg)] bg-no-repeat bg-cover bg-center backdrop-blur-md rounded-xl">
      <h2 className="text-center text-[2.5vh] font-bold text-red-600">
        Enhancement Failed
      </h2>
      <p className="text-red-500 text-[2.2vh] text-center">
        Couldn't enhance this image. Please try another one.
      </p>
    </div>
  );

  return (
    <div className="bg-[white] shadow-xl shadow-[gray] border border-[gray] p-[2vh] rounded-[3vh] w-full h-full">
      <>
        {!!successModal?.key && <SuccessModal
          isOpen={successModal}
          onRequestClose={() => {
            setSuccessModal(false)
          }}
        />}
        <div className="w-full grid grid-cols-[auto_2px_1fr] gap-[2vh] items-center mb-[2.5vh]">
          <h2 className="text-[2.5vh] font-bold text-blue-900">AI Image Enhancement</h2>
          <div className="w-[0.2vh] h-[3vh] bg-gray-400" />
          <p className="text-[2vh] text-black font-medium leading-relaxed">
            Our AI converts blurry, dark, or poor quality photos into clear, bright masterpieces.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-[2vh]">
          {/* Upload Container */}
          <div className="bg-slate-50 bg-opacity-40 rounded-[1.5vh] p-[1.5vh] border border-smd_golden-light flex flex-col">
            <div className="border-[0.3vh] border-gray-500 border-dashed rounded-[1vh] flex-1 flex flex-col justify-between">
              {isUploading ? (
                <ProgressBar />
              ) : imageValue.originalImage ? (
                <div className="flex justify-between items-center w-full h-full">
                  <div className="flex items-center gap-[1vh] min-w-0">
                    <Img
                      src={fileType === "png" ? "images/soomuch-demo/png.png" : "images/soomuch-demo/jpg.svg"}
                      className="h-[4vh] w-[4vh]"
                    />
                    <span className="text-[1.8vh] font-medium truncate">
                      {imageValue.originalImage.split('/').pop()}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setImageValue({
                        originalImage: "",
                        enhancedImage: "",
                        selectedImage: ""
                      });
                    }}
                    className="p-[0.5vh] hover:bg-gray-100 rounded"
                  >
                    <svg className="h-[3vh] w-[3vh]" viewBox="0 0 24 24">
                      <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" />
                      <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div {...getRootProps()} className="cursor-pointer h-full w-full flex flex-col items-center justify-center">
                  <input {...getInputProps()} />
                  <div className="flex flex-col items-center justify-center gap-[1vh]">
                    <svg className="w-[4vh] h-[4vh] text-blue-400" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-blue-600 text-[1.8vh] font-medium">Click here to upload</p>
                    <p className="text-gray-500 text-[1.5vh]">or drag and drop</p>
                  </div>
                </div>
              )}
            </div>

            {!isUploading && (
              <div className="text-center mt-[1.5vh]">
                <p className="text-[1.5vh] text-black mb-[1vh]">
                  Don't have images? Try our samples:
                </p>
                <div className="grid grid-cols-3 gap-[1vh]">
                  {[{ num: 0, color: "#ffffff" }, { num: 1, color: "#2eaad2" }, { num: 2, color: "#fd657c" }, { num: 3, color: "#f2a939" }]?.slice(1).map((num) => (
                    <button
                      key={num?.num}
                      onClick={() => handleSampleClick(num?.num)}
                      style={{
                        backgroundColor: num?.color
                        // ${+selectedSample === +num?.num  ? "bg-blue-300" : "bg-blue-200 bg-opacity-30"}
                      }}
                      className={`border border-blue-400 text-[white] py-[0.5vh] rounded-[0.5vh] text-[1.5vh]`}
                    >
                      {`Sample ${num?.num}`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Before Preview */}
          <div className="flex flex-col gap-[1vh]">
            <p className="text-[1.8vh] font-semibold text-center text-[red]">Before</p>
            <div className="rounded-[1.5vh] p-[1vh] border border-smd_golden-light">
              <div className="rounded-[1vh] overflow-hidden h-full">
                <Img
                  src={
                    selectedSample !== null
                      ? SAMPLES[selectedSample].before
                      : imageValue.originalImage || DEFAULT_BEFORE_IMAGE
                  }
                  className="w-full min-h-[40vh] max-h-[40vh] object-contain"
                />
              </div>
            </div>
          </div>

          {/* After Preview */}
          <div className="flex flex-col gap-[1vh]">
            <p className="text-[1.8vh] font-semibold text-center text-[green]">After</p>
            <div className=" rounded-[1.5vh] p-[1vh] border border-smd_golden-light">
              <div className="rounded-[1vh] overflow-hidden h-full">
                {(enhancementStatus.processing || dummyLoader) ? (analysisStep === 1 ? <ImageAnalysingScreen /> : <LowResolutionScreen />) :
                  (enhancementError ? <EnhancementErrorScreen /> : <Img
                    src={
                      selectedSample !== null
                        ? SAMPLES[selectedSample].after
                        : imageValue.enhancedImage || DEFAULT_AFTER_IMAGE
                    }
                    className="w-full min-h-[40vh] max-h-[40vh] object-contain"
                  />)}
              </div>
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

export default FotorImageUpload;